"use client";

import { useMemo, useState, useEffect, useCallback } from "react";
import Customisetable from "../Components/Table/Customisetable";
import useAuthStore from "../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../Zustand/Store/usePermissionDataStore";
import { coreHasPermission } from "./corePermissionUtils";
import { MAIN_URL } from "./Urls";
import { resolveDatagridScope } from "./coreSession";
import { useLatestValue } from "@dnd-kit/utilities";
import { useLocation } from "react-router-dom";
import CustomisetableAlter from "../Components/Table/CustomisetableAlter";
const inferDataType = (value) => {
  if (value instanceof Date) return "date";
  if (typeof value === "number") {
    return value.toString().includes(".") ? "currency" : "number";
  }
  if (typeof value === "string") {
    if (value.includes("@")) return "email";
    if (/^[0-9+\-() ]+$/.test(value)) return "phone";
    return "string";
  }
  return "string";
};

// Default configuration for different field types
const FIELD_DEFAULTS = {
  string: { width: 180, sortable: true, filterable: true },
  number: { width: 80, sortable: true, filterable: true },
  date: { width: 180, sortable: true, filterable: true },
  enum: { width: 100, sortable: false, filterable: true },
  email: { width: 180, sortable: false, filterable: true },
  phone: { width: 180, sortable: false, filterable: true },
  currency: { width: 180, sortable: true, filterable: true },
};

/**
 * Generate column configuration from data
 */
const generateColumnsFromData = (data, config) => {
  if (!data || data.length === 0) return [];

  const mandatoryColumns = config.mandatoryColumns || [];
  const defaultVisibleColumns = config.defaultVisibleColumns || [];
  const pinnedColumns = config.pinnedColumns || [];

  // Generate columns from first data row
  const columns = Object.entries(data[0] || {})
    .map(([key, value]) => {
      const lowerKey = key.toLowerCase();

      // Skip system fields (IDs, timestamps, objects)
      if (
        lowerKey.endsWith("_id") ||
        key === "id" ||
        lowerKey.includes("updated_at") ||
        lowerKey.includes("created_at") ||
        lowerKey.includes("created_by") ||
        typeof value === "object"
      ) {
        return null;
      }

      const dataType = inferDataType(value);
      const defaults = FIELD_DEFAULTS[dataType];

      return {
        field: key,
        key,
        label: key
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        dataType,
        width: defaults.width,
        sortable: defaults.sortable,
        filterable: defaults.filterable,
        mandatory: mandatoryColumns.includes(key),
        defaultVisible: defaultVisibleColumns.includes(key),
        pinned: pinnedColumns.includes(key),
      };
    })
    .filter(Boolean);

  // Sort columns: employee_name first, then codes, then other names, then others
  const employeeNameField = columns.filter(
    (col) => col.key.toLowerCase() === "employee_name"
  );
  const codeFields = columns.filter(
    (col) =>
      col.key.toLowerCase().includes("code") &&
      col.key.toLowerCase() !== "employee_name"
  );
  const otherNameFields = columns.filter(
    (col) =>
      col.key.toLowerCase().includes("name") &&
      col.key.toLowerCase() !== "employee_name"
  );
  const otherFields = columns.filter(
    (col) =>
      !col.key.toLowerCase().includes("name") &&
      !col.key.toLowerCase().includes("code")
  );

  // Sort each group alphabetically
  codeFields.sort((a, b) => a.label.localeCompare(b.label));
  otherNameFields.sort((a, b) => a.label.localeCompare(b.label));
  otherFields.sort((a, b) => a.label.localeCompare(b.label));

  return [
    ...employeeNameField,
    ...codeFields,
    ...otherNameFields,
    ...otherFields,
  ];
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function TableDataGeneric({
  tableName,
  primaryKey,
  heading,
  data,
  sortname,
  Route,
  DeleteFunc,
  token,
  EditFunc,
  handleShow,
  showActions,
  linkType,
  title,
  hideToolbar,
  recordKey,
  onclickRow,
  CardData,
  configss,
  filterModule,
  onFilterChange,
  onSortChange,
  onPageChange,
  renderCell,
  config = {},
  onColumnWidthChange,
  onColumnVisibilityChange,
  organizationUserId,
  showLayoutButtons,
  paginationData,
  setPaginationData,
  edit_delete_action,
  enableRowSelection = true,
}) {
  // ============================================================================
  // STATE & HOOKS
  // ============================================================================

  const { userData } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const [loadedBackendConfig, setLoadedBackendConfig] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [configSource, setConfigSource] = useState(null);

  // Permission checks for edit and delete actions
  const hasEditPermission = useMemo(() => {
    if (!edit_delete_action || !Array.isArray(edit_delete_action) || edit_delete_action.length === 0) {
      return true; // If no permission specified, allow by default
    }
    const editPermission = edit_delete_action[0]; // First element is edit permission
    return coreHasPermission(Permission, editPermission);
  }, [edit_delete_action, Permission]);

  const hasDeletePermission = useMemo(() => {
    if (!edit_delete_action || !Array.isArray(edit_delete_action) || edit_delete_action.length < 2) {
      return true; // If no permission specified, allow by default
    }
    const deletePermission = edit_delete_action[1]; // Second element is delete permission
    return coreHasPermission(Permission, deletePermission);
  }, [edit_delete_action, Permission]);

  const location = useLocation();

  // ============================================================================
  // EXTRACT ORGANIZATION ID
  // ============================================================================

  const datagridScope = useMemo(
    () => resolveDatagridScope(userData),
    [userData]
  );

  const orgId = datagridScope.orgId;
  const datagridKeySuffix = datagridScope.datagridKeySuffix;
  const scopedOrganizationUserId =
    organizationUserId ?? datagridScope.organizationUserId;

  const fromEmployeeList = location.state?.fromEmployeeList || false;

  // ============================================================================
  // API FUNCTIONS
  // ============================================================================

  /**
   * Load saved table layout from backend using POST method with context
   * Priority: Organization User > Organization > General
   */
  const loadLayoutFromBackend = useCallback(async () => {
    if (!tableName || !datagridKeySuffix) {
      console.warn("Missing tableName or datagrid scope, skipping layout fetch");
      setIsLoadingConfig(false);
      return;
    }

    const datagridKey = `${tableName}_grid_${datagridKeySuffix}`;

    try {
      console.log(`Fetching saved layout for: ${datagridKey}`);

      // Build request payload with priority context
      const requestPayload = {
        datagrid_key: datagridKey,
      };

      // Add organization_user_id if available (highest priority)
      if (scopedOrganizationUserId) {
        requestPayload.organization_user_id = scopedOrganizationUserId;
        console.log("Including organization_user_id:", scopedOrganizationUserId);
      }

      // Add organization_id (second priority)
      if (orgId) {
        requestPayload.organization_id = orgId;
        console.log("Including organization_id:", orgId);
      }

      // Use POST method with context in body
      const response = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(requestPayload),
      });

      if (response.ok) {
        const responseData = await response.json();

        console.log("API Response:", responseData);

        // Response format: { message: "...", type: "organization_user" | "organization" | "general", datagrid: {...} }
        if (responseData.datagrid) {
          const savedConfig =
            responseData.datagrid.datagrid_default_configuration;

          if (savedConfig) {
            setLoadedBackendConfig(savedConfig);
            setConfigSource(responseData.type); // Track source
          } else {
            console.log("No configuration found in datagrid");
            setLoadedBackendConfig(null);
            setConfigSource(null);
          }
        } else {
          console.log("No datagrid in response");
          setLoadedBackendConfig(null);
          setConfigSource(null);
        }
      } else if (response.status === 404) {
        console.log("No saved layout found (404), using default configuration");
        setLoadedBackendConfig(null);
        setConfigSource(null);
      } else if (response.status === 422) {
        const errorData = await response.json();
        console.error("Validation errors:", errorData.errors);
        setLoadedBackendConfig(null);
        setConfigSource(null);
      } else {
        console.error(`Failed to fetch layout: ${response.status}`);
        setLoadedBackendConfig(null);
        setConfigSource(null);
      }
    } catch (error) {
      console.error("Error loading saved layout:", error);
      setLoadedBackendConfig(null);
      setConfigSource(null);
    } finally {
      setIsLoadingConfig(false);
    }
  }, [orgId, datagridKeySuffix, tableName, token, scopedOrganizationUserId]);

  /**
   * Save current table layout to backend
   * Saves to organization_user level if organizationUserId is available,
   * otherwise saves to organization level
   */
  const saveLayoutToBackend = useCallback(
    async ({ tableName: tbl, layoutData }) => {
      if (!datagridKeySuffix) {
        const error = "Cannot save layout: datagrid scope not found";
        console.error(error);
        throw new Error(error);
      }

      const payload = {
        datagrid_key: `${tableName}_grid_${datagridKeySuffix}`,
        datagrid_default_configuration: layoutData,
      };

      if (orgId) {
        payload.organization_id = orgId;
        payload.organization_entity_id = userData?.organization_entity_id || null;
      }

      if (scopedOrganizationUserId) {
        payload.organization_user_id = scopedOrganizationUserId;
        console.log(
          "Saving layout to organization user level with ID:",
          scopedOrganizationUserId
        );
      } else if (orgId) {
        console.log("Saving layout to organization level");
      } else {
        console.log("Saving layout to core platform scope");
      }

      console.log("Saving layout...", payload);

      try {
        const saveUrl = orgId
          ? `${MAIN_URL}/api/organization-datagrids`
          : `${MAIN_URL}/api/general-datagrids`;
        const response = await fetch(saveUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => "");
          let errorMessage = `Save failed: ${response.status}`;

          try {
            const errorJson = JSON.parse(errorText);
            errorMessage += errorJson.errors
              ? ` - ${JSON.stringify(errorJson.errors)}`
              : errorJson.message
                ? ` - ${errorJson.message}`
                : "";
          } catch (e) {
            errorMessage += errorText ? ` - ${errorText}` : "";
          }

          console.error(errorMessage);
          throw new Error(errorMessage);
        }

        const responseData = await response.json();
        console.log("✓ Layout saved successfully", responseData);

        // Update local state with saved configuration
        setLoadedBackendConfig(layoutData);
        // Set config source based on whether it was saved to user or org level
        setConfigSource(
          scopedOrganizationUserId
            ? "organization_user"
            : orgId
              ? "organization"
              : "general"
        );

        return true;
      } catch (error) {
        console.error("Error saving layout:", error);
        throw error;
      }
    },
    [orgId, datagridKeySuffix, userData, tableName, token, scopedOrganizationUserId]
  );

  /**
   * Reset table layout to default configuration
   * If currently using organization_user config, resets to organization level
   * If currently using organization config, resets to general default
   */
  const resetLayoutToDefault = useCallback(
    async ({ tableName: tbl }) => {
      if (!datagridKeySuffix) {
        const error = "Cannot reset layout: datagrid scope not found";
        console.error(error);
        throw new Error(error);
      }

      try {
        const datagridKey = `${tableName}_grid_${datagridKeySuffix}`;

        const params = new URLSearchParams({
          datagrid_key: datagridKey,
        });

        if (orgId) {
          params.append("organization_id", orgId.toString());
        }

        if (scopedOrganizationUserId && configSource === "organization_user") {
          params.append("organization_user_id", scopedOrganizationUserId.toString());
          console.log(
            "Resetting organization user level config to organization level..."
          );
        } else {
          console.log(
            "Resetting organization level config to general default..."
          );
        }

        const response = await fetch(
          `${MAIN_URL}/api/datagrid/get-by-context?${params.toString()}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );

        if (!response.ok) {
          const errorText = await response.text().catch(() => "");
          let errorMessage = `Reset failed: ${response.status}`;

          try {
            const errorJson = JSON.parse(errorText);
            errorMessage += errorJson.errors
              ? ` - ${JSON.stringify(errorJson.errors)}`
              : errorJson.message
                ? ` - ${errorJson.message}`
                : "";
          } catch (e) {
            errorMessage += errorText ? ` - ${errorText}` : "";
          }

          console.error(errorMessage);
          throw new Error(errorMessage);
        }

        console.log("✓ Layout reset successfully");

        // Reload configuration from backend to get the next level (org or general)
        await loadLayoutFromBackend();

        return true;
      } catch (error) {
        console.error("Error resetting layout:", error);
        throw error;
      }
    },
    [
      orgId,
      datagridKeySuffix,
      token,
      tableName,
      scopedOrganizationUserId,
      configSource,
      loadLayoutFromBackend,
    ]
  );

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Load saved layout on component mount
  useEffect(() => {
    loadLayoutFromBackend();
  }, [loadLayoutFromBackend]);

  // ============================================================================
  // COLUMN PROCESSING
  // ============================================================================

  /**
   * Process columns based on priority:
   * 1. Real-time configuration from form (configss prop)
   * 2. Loaded backend configuration (Organization User > Organization > General)
   * 3. Generated from data
   */
  const processedColumns = useMemo(() => {
    // Don't process until config is loaded
    if (isLoadingConfig) {
      return null;
    }

    // Priority 1: Use real-time configuration from form
    if (configss?.length > 0) {
      return configss.map((col, index) => ({
        field: col.field,
        key: col.field,
        label: col.label,
        dataType: col.dataType || inferDataType(""),
        width: col.width,
        sortable: col.sortable,
        filterable: col.filterable,
        mandatory: col.required,
        defaultVisible: col.visible,
        pinned: col.pinned === "left",
        visible: col.visible,
        order:
          col.field === "Employee_Code"
            ? 0
            : col.field === "Name"
              ? 1
              : index + 2,
      }));
    }

    // Priority 2: Use loaded backend configuration (with fallback hierarchy)
    if (loadedBackendConfig?.columns) {
      console.log(
        `Using loaded backend configuration from ${configSource} level for columns`
      );
      return loadedBackendConfig.columns.map((col) => ({
        ...col,
        key: col.key || col.field,
        field: col.field || col.key,
      }));
    }

    // Priority 3: Generate from data
    console.log("Generating columns from data (no saved config found)");
    const generatedColumns = generateColumnsFromData(data, config);

    // ✅ Ensure generated columns have all required properties
    return generatedColumns.map((col, index) => ({
      ...col,
      visible:
        col.defaultVisible !== undefined ? col.defaultVisible : index < 4,
      order: index,
    }));
  }, [
    configss,
    loadedBackendConfig,
    data,
    config,
    isLoadingConfig,
    configSource,
  ]);

  // ============================================================================
  // TABLE CONFIGURATION
  // ============================================================================

  const tableConfiguration = useMemo(() => {
    // Don't build configuration until columns are processed
    if (
      !processedColumns ||
      (processedColumns.length === 0 && isLoadingConfig)
    ) {
      return [];
    }

    // Use backend config if available, otherwise use defaults
    const sortConfig =
      loadedBackendConfig?.sortConfig ||
      (config.sort
        ? [{ key: config.sort.field, direction: config.sort.direction }]
        : [{ key: sortname, direction: "desc" }]);

    const rowsPerPage =
      loadedBackendConfig?.rowsPerPage || config.pagination?.rowsPerPage || 10;

    const filters = loadedBackendConfig?.filters || config.filters || {};

    const search = loadedBackendConfig?.search ||
      config.search || {
        enabled: true,
        placeholder: "Search...",
        fields: [],
      };

    return [
      {
        table: tableName,
        primaryKey: primaryKey,
        heading: heading,
        lastModified: new Date().toISOString(),
        configSource: configSource, // Include config source
        default_config: {
          columns: processedColumns.map((col, index) => ({
            ...col,
            visible:
              col.visible !== undefined
                ? col.visible
                : (config.defaultVisibleColumns || []).includes(col.field) ||
                  index < 4,
            order:
              col.order !== undefined
                ? col.order
                : col.field === "Employee_Code"
                  ? 0
                  : col.field === "Name"
                    ? 1
                    : config.defaultVisibleColumns?.indexOf(col.field) ?? index,
          })),
          sortConfig,
          rowsPerPage,
          filters,
          search,
        },
        user_config: {
          columns: processedColumns.map((col, index) => ({
            ...col,
            key: col.key,
            field: col.field,
            visible:
              col.visible !== undefined
                ? col.visible
                : (config.defaultVisibleColumns || []).includes(col.field) ||
                  index < 4,
            order:
              col.order !== undefined
                ? col.order
                : col.field === "Employee_Code"
                  ? 0
                  : col.field === "Name"
                    ? 1
                    : config.defaultVisibleColumns?.indexOf(col.field) ?? index,
            width: col.width,
            sortDirection: null,
            filterValue: null,
            pinned:
              col.pinned !== undefined
                ? col.pinned
                : col.field === "Employee_Code" || col.field === "Name",
            dataType: col.dataType,
          })),
          sortConfig,
          rowsPerPage,
          filters,
          search,
          lastModified: new Date().toISOString(),
        },
      },
    ];
  }, [
    processedColumns,
    tableName,
    primaryKey,
    heading,
    config,
    sortname,
    loadedBackendConfig,
    isLoadingConfig,
    configSource,
  ]);

  // ============================================================================
  // RENDER
  // ============================================================================

  // Don't render table until configuration is loaded
  if (isLoadingConfig) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <p>Loading table configuration...</p>
      </div>
    );
  }

  return (
    <>
      {tableName == "Employees" || tableName === "organizations" || tableName === "client_payments" ||  tableName === "application_users" ||  tableName === "organization_client_contacts" || tableName === "organization_clients" || tableName === "clients" ||  tableName === "client_licenses" || tableName === "application_editions" || tableName === "application_user_roles" || tableName === "permission_assignment" || tableName === "user_role_assignment" || tableName === "assigned_client_applications" || tableName === "assigned_organization_applications" || tableName == "employee_leaves" || tableName == "employee_exits" || tableName == "employee_employment_records" || tableName == "employee_functional_roles" || tableName == "organization_employment_stages" || tableName == "organization_work_shifts" || tableName == "employee_work_shift_assignments" || tableName == "organization_work_shift_rotation_patterns" || tableName == "organization_work_shift_rotation_days" || tableName == "employee_work_shift_rotation_assignments" || tableName == "organization_person_attendance_timelogs" || tableName == "organization_person_attendance_records" || tableName == "organization_employee_increment_types" || tableName == "departments" || tableName == "organization_designations" || tableName == "organization_locations" || tableName == "organization_units" || tableName == "organization_designations" || tableName == "organization_business_registrations" || tableName == "organization_attendance_status_types" || tableName == "organization_attendance_deviation_reason_types" || tableName == "organization_attendance_deviation_reasons" || tableName == "organization_attendance_break_types" || tableName == "organization_attendance_sources" || tableName == "organization_attendance_breaks" || tableName == "organization_work_shift_breaks" || tableName == "organization_work_shift_rotation_patterns" || tableName == "organization_work_shift_rotation_days" || tableName == "organization_leave_policies" || tableName == "organization_functional_role_specializations" || tableName == "organization_functional_roles" || tableName == "organization_business_ownership_types" || tableName == "organization_business_registration_types" || tableName == "organization_residential_ownership_types" || tableName == "organization_location_ownership_types" || 
      tableName == "organization_employee_address_types" || 
      tableName == "organization_employment_statuses" || tableName == "organization_person_leave_type_monthly_summaries" || tableName == "organization_person_leave_category_monthly_summaries" ||  tableName == "organization_employment_types" || tableName == "organization_employment_exit_reasons" || tableName == "organization_employment_exit_reason_types" || tableName == "organization_employment_categories" || tableName == "organization_holiday_types"  || tableName == "organization_leave_categories" || tableName == "organization_leave_types" || tableName == "organization_leave_reason_types" || tableName == "organization_leave_reasons" || tableName == "organization_work_shift_types" || tableName == "organization_work_models" || tableName == "organization_languages" || tableName == "organization_work_shift_rotation_days" || tableName == "organization_unit_types" || tableName == "organization_functional_role_skill_elements" || tableName == "organization_functional_role_skills" || tableName == "organization_learning_provider_types" || tableName == "organization_learning_providers" || tableName == "organization_learning_resources" || tableName == "organization_learning_resource_functional_roles" || tableName == "organization_learning_resource_skill_elements" ||
      tableName == "organization_roles" ||  tableName === "employee_increments"  || tableName === "intern_exit_records"||
       tableName == "organization_internship_types" ||  tableName === "Users" ||  tableName === "intern_leave_type_monthly_summaries" ||  tableName === "intern_leave_type_monthly_summaries" ||  tableName === "intern_leave_monthly_summaries"  || tableName === "intern_attendance_records" ||  tableName === "intern_leaves"   || tableName === "intern_attendance_timelogs"||  tableName == "organization_internship_statuses" || tableName == "organization_internship_stages" || tableName == "employee_document_types" || tableName == "organization_user_types" || tableName == "interns" || tableName == "employee_documents" || tableName == "organization_work_shift_days" || tableName == "organization_work_model_days" || tableName == "intern_document_types" || tableName == "organization_holiday_calendars" || tableName == "organization_payroll_cycles" || tableName == "organization_payroll_component_types" || tableName == "organization_payroll_components" || tableName == "organization_payroll_component_slabs" || tableName == "organization_payroll_employee_salary_structures" || tableName == "organization_payroll_employee_salary_structure_components" || tableName == "organization_payroll_advances" || tableName == "organization_payroll_loan_types" || tableName == "organization_payroll_loans" || tableName == "organization_payroll_loan_transactions" || tableName == "organization_payroll_securities" || tableName == "organization_payroll_security_transactions" || tableName === "organization_payroll_periods" || tableName === "organization_payroll_runs"  || tableName === "organization_payroll_run_employees" || tableName=== "organization_payroll_run_employee_components" || tableName === "organization_payroll_reimbursements" || tableName === "organization_payroll_reimbursement_types" || tableName === "organization_payroll_adjustments" || tableName === "organization_payroll_adjustment_types" || tableName === "organization_payroll_journal_entries" || tableName === "organization_person_identification_types" || tableName === "organization_leave_entitlements" || tableName === "Interns" || tableName === "intern_stipends" || tableName === "Intern Certificate"  || tableName === "intern_certificates"  || tableName === "intern_functional_roles" || tableName === "intern_functional_roles" || tableName === "organization_person_leave_monthly_summaries" || tableName === "employee_leave_type_monthly_summaries" || tableName === "intern_leave_category_monthly_summaries"  || tableName === "employee_leave_category_monthly_summaries" ||   tableName === "intern_leave_balances" || tableName === "employee_leave_balances" || tableName === "organization_person_leave_category_monthly_summaries" ? (
        <Customisetable
          configuration={tableConfiguration}
          tableName={tableName}
          Route={Route}
          DeleteFunc={hasDeletePermission ? DeleteFunc : undefined}
          EditFunc={hasEditPermission ? EditFunc : undefined}
          hasEditPermission={hasEditPermission}
          hasDeletePermission={hasDeletePermission}
          handleShow={handleShow}
          token={token}
          mainKey={primaryKey}
          mainData={data}
          recordKey={recordKey}
          showActions={showActions}
          hideToolbar={hideToolbar}
          CardColoumn={CardData}
          sortname={sortname}
          onSortChange={onSortChange}
          onFilterChange={onFilterChange}
          onPageChange={onPageChange}
          filterModule={filterModule}
          configss={configss}
          renderCell={renderCell}
          onColumnWidthChange={onColumnWidthChange}
          onColumnVisibilityChange={onColumnVisibilityChange}
          linkType={linkType}
          title={title}
          onclickRow={onclickRow}
          onSaveLayout={saveLayoutToBackend}
          onResetLayout={resetLayoutToDefault}
          loadedBackendConfig={loadedBackendConfig}
          configSource={configSource}
          showLayoutButtons={true}
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          edit_delete_action={edit_delete_action}
          enableRowSelection={enableRowSelection}
        />
      ) : (
        <CustomisetableAlter
          configuration={tableConfiguration}
          tableName={tableName}
          Route={Route}
          DeleteFunc={hasDeletePermission ? DeleteFunc : undefined}
          EditFunc={hasEditPermission ? EditFunc : undefined}
          hasEditPermission={hasEditPermission}
          hasDeletePermission={hasDeletePermission}
          handleShow={handleShow}
          token={token}
          mainKey={primaryKey}
          mainData={data}
          showActions={showActions}
          hideToolbar={hideToolbar}
          CardColoumn={CardData}
          sortname={sortname}
          onSortChange={onSortChange}
          onFilterChange={onFilterChange}
          configss={configss}
          renderCell={renderCell}
          onColumnWidthChange={onColumnWidthChange}
          onColumnVisibilityChange={onColumnVisibilityChange}
          linkType={linkType}
          title={title}
          onclickRow={onclickRow}
          onSaveLayout={saveLayoutToBackend}
          onResetLayout={resetLayoutToDefault}
          loadedBackendConfig={loadedBackendConfig}
          configSource={configSource}
          showLayoutButtons={true}
          paginationData={paginationData}
          edit_delete_action={edit_delete_action}
          enableRowSelection={enableRowSelection}
        />
      )}
    </>
  );
}


export default TableDataGeneric;
